import React from 'react'
import { usePodcastEditor } from '../../context/use-podcast-editor'
import { useConfirm } from '../../../../context/confirm-context'

import { Skeleton } from '../../../../components/ui/skeleton'
import { LanguagePicker } from '../../../../components/language-picker'
import Tooltip from '../../../../components/ui/tooltip'

interface LanguageSelectorProps {
  className?: string
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({ className = '' }) => {
  const { podcast, languageIso, setLanguageIso, isLoading, isGeneratingAudio, isGeneratingScript } = usePodcastEditor()
  const { confirm } = useConfirm()
  const [pending, setPending] = React.useState(false)

  React.useEffect(() => {
    const handleCloned = (e: Event) => {
      const detail = (e as CustomEvent).detail as { clonedPodcastId: string }
      setPending(false)
      // Navigate to new podcast editor page
      if (detail?.clonedPodcastId) {
        window.location.assign(`/podcasts/${detail.clonedPodcastId}`)
      }
    }
    const handleCloneError = (e: Event) => {
      const detail = (e as CustomEvent).detail as { message: string }
      setPending(false)
      alert(detail?.message || 'Failed to create language copy.')
    }
    window.addEventListener('podcast:cloned', handleCloned as EventListener)
    window.addEventListener('podcast:clone-error', handleCloneError as EventListener)
    return () => {
      window.removeEventListener('podcast:cloned', handleCloned as EventListener)
      window.removeEventListener('podcast:clone-error', handleCloneError as EventListener)
    }
  }, [])

  const isBusy = isGeneratingAudio || isGeneratingScript

  const handleBeforeChange = React.useCallback(async (nextIso: string): Promise<boolean> => {
    if (!podcast) return false
    if (nextIso === languageIso) return false
    return await confirm({
      title: 'Create language version?',
      description: 'A podcast\'s language is permanent. To change language we create a new version using the original source. The current podcast stays untouched.',
      confirmText: 'Create copy',
      cancelText: 'Cancel',
      variant: 'default'
    })
  }, [podcast, languageIso, confirm])

  const handleChange = async (iso: string) => {
    try {
      setPending(true)
      await setLanguageIso(iso)
    } catch {
      setPending(false)
    }
  }

  if (isLoading) {
    return (
      <div className={`space-y-2 ${className}`}>
        <div className="flex items-center justify-between">
          <Skeleton height="4" width="20" />
        </div>
        <div className="h-10 rounded border border-black/10 bg-white p-2">
          <Skeleton height="4" width="24" />
        </div>
      </div>
    )
  }

  const disabled = pending || isBusy
  const tooltipContent = isBusy ? 'Cannot change language while script or audio is being generated.' : null

  const picker = (
    <LanguagePicker
      value={languageIso}
      onChange={handleChange}
      onBeforeChange={handleBeforeChange}
      disabled={disabled}
      size="md"
    />
  )

  return (
    <div className={`space-y-2 ${className}`} data-tourid="language-selector">
      <label className="text-sm font-medium text-foreground">Language</label>
      {tooltipContent ? (
        <Tooltip content={tooltipContent} side="top" align="start">
          <div className="w-full">{picker}</div>
        </Tooltip>
      ) : (
        <div className="w-full">{picker}</div>
      )}
    </div>
  )
}
